/* istanbul ignore file - better tested visually */

import { usePrevious } from '@partful/shared/lib/hooks/usePrevious';
import { invalidate, useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimationAction, AnimationMixer, Mesh, Object3D } from 'three';
import { addAnimationToScene } from '../../animations/lib/addAnimationToScene';
import { useAnimationFrameEffect } from '../../animations/lib/useAnimationFrameEffect';
import { Animation } from '../../animations/model/animation';
import { AnimationState } from '../../animations/model/animationState';
import { Highlight } from '../Highlight/Highlight';

export interface ModelAnimatorProps {
  animation: Animation;
  time?: number;
  state?: AnimationState;
  loop?: boolean;
  disableHighlight?: boolean;
  onTick?: (time: number) => void;
  onFinished?: () => void;
}

export function ModelAnimator({
  animation,
  time,
  state = AnimationState.Stopped,
  loop,
  disableHighlight = false,
  onTick,
  onFinished,
}: ModelAnimatorProps) {
  const scene = useThree(({ scene }) => scene);
  const [mixer, setMixer] = useState<AnimationMixer>();
  const [actions, setActions] = useState<AnimationAction[]>([]);
  const previousState = usePrevious(state);

  // The maximum length of all animation channels.
  const maxLength = useMemo(
    () =>
      Math.max(
        ...Object.values(animation.channels).flatMap((channel) =>
          channel.keyframes.flatMap((keyframe) => keyframe.time)
        )
      ),
    [animation.channels]
  );

  const highlighted = useMemo<Object3D[]>(
    () =>
      Object.entries(animation.channels).reduce<Object3D[]>((out, [name, channel]) => {
        // Early out if not trying to highlight this channel object
        if (channel.highlighted === false) return out;

        // Traverse the matching object and add itself and all meshes below
        scene.getObjectByName(name)?.traverseVisible((object) => {
          if (object instanceof Mesh && out.includes(object) === false) {
            out.push(object);
          }
        });

        return out;
      }, []),
    [animation.channels, scene]
  );

  // Sets the time of the mixer to a given value.
  const setTime = useCallback(
    (time: number) => {
      for (const action of actions) {
        action.reset();
      }
      if (mixer) {
        mixer.setTime(time);
      }
    },
    [actions, mixer]
  );

  useAnimationFrameEffect(() => {
    // Request animation frame to update animators. This ensures that the scene
    // has got all it's deeply nested children ready.
    const { mixer, actions } = addAnimationToScene(scene, animation);
    setMixer(mixer);
    setActions(actions);

    // Ensure the changes are reflected in the rendered scene
    invalidate();
  }, [animation, scene]);

  // Set up finished listeners and clean them up when the component unmounts.
  useEffect(() => {
    if ((!onFinished && !loop) || state !== AnimationState.Playing) return;
    if (time && time > maxLength) {
      setTime(0);
      if (!loop) {
        onFinished?.();
      }
    }
  }, [loop, maxLength, onFinished, setTime, state, time]);

  // Sets the animation to a specific time if not playing.
  useEffect(() => {
    if (time != null && state !== AnimationState.Playing) {
      setTime(time);
    }
  }, [time, state, setTime]);

  // Resets the animation to it's initial state on stop.
  useEffect(() => {
    if (previousState !== AnimationState.Stopped && state === AnimationState.Stopped) {
      setTime(0);
      onTick?.(0);
    }
  }, [setTime, onTick, previousState, state]);

  // Advances the animation on each frame, by the delta time between frames.
  useFrame((_, delta) => {
    if (state !== AnimationState.Playing) return;
    if (mixer) {
      mixer.update(delta);
      onTick?.(mixer.time);
    }
  });

  return disableHighlight ? null : <Highlight selection={highlighted} />;
}
